import { Injectable } from '@angular/core';
import { DatePipe, formatDate } from '@angular/common';
import { FilterUtils } from 'primeng/utils';

import { LocalStorageService } from './local-storage.service';
import {
  CreateGridCompFeedInApplicationResponse,
  CreateInstallationApplicationResponse,
  LookupOptionsData,
} from '../jurisdiction/jurisdiction.domain';
import {
  GetGridCompFeedInApplicationObject,
  GetInstallationApplicationObject,
  GridCompFeedInApplicationData,
  InstallationApplicationData,
} from '../jurisdiction/facilities-form/facilities-form.domain';
import { Observable, of } from 'rxjs';
import { FacilityFormType, GridConnectionApplicationType, InstallationFlowType, TechnicianFormType } from '../constants/app.constants';
import { UrlSegments } from '../jurisdiction/jurisdiction.constants';
import { CommunicationService } from './communication.service';
import {
  CreateGridConnectionRequest,
  CreateGridConnectionResponse,
  GetGridConnectionObject,
  GridConnectionData,
  GridConnectionIds,
} from '../jurisdiction/technician-data/technician-data.domain';
import { OrderProcessApplication } from '../jurisdiction-overview/jurisdiction-overview.domain';
import { FormTypesData, TechnicianData, UpdateLockStatusResponse } from '../domains/common.domain';
import { EnvironmentService } from './environment.service';
import { OrderHistoryResponse } from '../+shared/components/order-history/order-history.domain';
import { RegistrationOfEnergyProducerApplication } from '../+regep/entities/regep-application';
import { SkStatus } from '../+shared/enums/skStatus';
import { AppLocation } from '../+regep/entities/app-location';
import { DrrCcdDeviceApplicationDto } from '../+drr-ccd-devices/technical-data/technical-data.domain';
import { map } from 'rxjs/operators';
import { parseBoolelan } from '../+shared/helpers/parsers.helper';

@Injectable()
export class CommonService {
  takeFromRequestOne: boolean;
  private applicationLogDate: string;
  private installationData: InstallationApplicationData;
  private gridCompFeedInData: GridCompFeedInApplicationData;
  private gridConnectionData: GridConnectionData;
  private gridConnectionIds: GridConnectionIds;
  private highlightRow: number;
  private orderProcessApplicationData: OrderProcessApplication;
  private locationApplicationData: any;
  private technicianDataObject: TechnicianData;

  get showHighlightedRow(): number {
    return this.highlightRow;
  }

  set showHighlightedRow(index: number) {
    this.highlightRow = index;
  }

  get installationApplicationData(): InstallationApplicationData {
    return this.installationData;
  }

  set installationApplicationData(data: InstallationApplicationData) {
    this.installationData = data;

    if (data) {
      this.localStorageService.setItem('connection_object_id', data.connectionobject.connectionobject_id);
      this.localStorageService.setItem('business_partner_id', data.businesspartner.businesspartner_id);
      this.localStorageService.setItem('gas_installation_id', data.gasinstallation.installation_id);
      this.localStorageService.setItem('heating_installation_id', data.districtheatinstallation.installation_id);
      this.localStorageService.setItem('power_installation_id', data.powerinstallation.installation_id);
      this.localStorageService.setItem('water_installation_id', data.waterinstallation.installation_id);
    }
  }

  get gridCompFeedInApplicationData(): GridCompFeedInApplicationData {
    return this.gridCompFeedInData;
  }

  set gridCompFeedInApplicationData(data: GridCompFeedInApplicationData) {
    this.gridCompFeedInData = data;

    if (data) {
      this.localStorageService.setItem('connection_object_id', data.connectionobject.connectionobject_id);
      this.localStorageService.setItem('business_partner_id', data.businesspartner.businesspartner_id);
    }
  }

  get locationData(): any {
    return this.locationApplicationData;
  }

  set locationData(data: any) {
    this.locationApplicationData = data;
  }

  get orderProcessData(): OrderProcessApplication {
    return this.orderProcessApplicationData;
  }

  set orderProcessData(data: OrderProcessApplication) {
    this.orderProcessApplicationData = data;
  }

  get allGridConnectionIds() {
    return this.gridConnectionIds;
  }

  get gridConnectionApplicationData() {
    return this.gridConnectionData;
  }

  set gridConnectionApplicationData(data: GridConnectionData) {
    this.gridConnectionData = data;

    if (data) {
      this.gridConnectionIds = {
        businessPartnerId: data?.businesspartner?.businesspartner_id,
        connectionObjectId: data?.connectionobject?.connectionobject_id,
        gasConnectionId: data?.gasgridcon?.gridcon_id,
        heatingConnectionId: data?.districtheatgridcon?.gridcon_id,
        powerConnectionId: data?.powergridcon?.gridcon_id,
        waterConnectionId: data?.watergridcon?.gridcon_id,
      };
    }
  }

  get technicianData(): TechnicianData {
    return this.technicianDataObject;
  }

  set technicianData(data: TechnicianData) {
    this.technicianDataObject = data;
  }

  get griConnectionApplicationId(): string {
    return this.gridConnectionData?.application_ext_id || this.gridConnectionData?.application_id;
  }

  get installationApplicationId(): string {
    return this.installationData?.application_ext_id || this.installationData?.application_id;
  }

  get applicationSubmissionDate(): string {
    return this.applicationLogDate;
  }

  set applicationSubmissionDate(date: string) {
    this.applicationLogDate = date.length ? formatDate(date, 'yyyy-MM-dd', 'en-US') : '';
  }

  constructor(
    private communicationService: CommunicationService,
    private environmentService: EnvironmentService,
    private localStorageService: LocalStorageService,
    private datePipe: DatePipe
  ) {
    this.installationData = null;
    this.highlightRow = -1;
    this.takeFromRequestOne = false;
    this.technicianData = null;
  }

  private static getInstallationApplicationType(selectedFlowType: string): string {
    switch (selectedFlowType) {
      case InstallationFlowType.NewInstallation:
        return GridConnectionApplicationType.New;
      case InstallationFlowType.UpdateInstallation:
        return GridConnectionApplicationType.Change;
      case InstallationFlowType.FeedInSystem:
        return GridConnectionApplicationType.FeedInSystem;
      case InstallationFlowType.SiteElectricityMeterdata:
        return GridConnectionApplicationType.SiteElectricityMeterdata;
      case InstallationFlowType.GridCompatibility:
        return GridConnectionApplicationType.GridCompatibility;
    }
  }

  getDropdownData(type: string): LookupOptionsData[] {
    const parsedDropdownData: LookupOptionsData[] = [];
    const lookupData = this.localStorageService.getItem('lookup_data');
    if (lookupData) {
      const dropdownOptions = lookupData[type] && lookupData[type].options;
      const optionsKeys = (lookupData[type] && Object.keys(lookupData[type].options)) || [];
      if (optionsKeys.length) {
        optionsKeys.forEach(key => {
          parsedDropdownData.push({
            label: dropdownOptions[key].name,
            order: dropdownOptions[key].order,
            value: key,
          });
        });
      }
    }

    if (parsedDropdownData.length) {
      parsedDropdownData.sort((valueOne, valueTwo) => {
        return valueOne.order - valueTwo.order;
      });
    }
    return parsedDropdownData;
  }

  getExistingInstallationApplications(selectedFlowType: string): Observable<GetInstallationApplicationObject> {
    const applicationType = CommonService.getInstallationApplicationType(selectedFlowType);
    return this.communicationService.getData(`${UrlSegments.ExistingInstallationApplication}${applicationType}`);
  }

  getExistingGridCompFeedInApplications(selectedFlowType: string): Observable<GridCompFeedInApplicationData[]> {
    const applicationType = CommonService.getGridCompFeedInApplicationType(selectedFlowType);
    return this.communicationService.getData(`${UrlSegments.ExistingGridCompFeedInApplication}${applicationType}`);
  }

  getGridCompFeedInApplicationById(applicationId: string): Observable<GridCompFeedInApplicationData[]> {
    return this.communicationService.getData(`${UrlSegments.GridCompFeedInApplication}${applicationId}`);
  }

  getExistingRegepApplications(skStatus: SkStatus): Observable<RegistrationOfEnergyProducerApplication[]> {
    return this.communicationService.getData(`${UrlSegments.RegistrationOfEnergyProducerApplicationByStatus}/${skStatus}`);
  }

  getExistingConsumptionDeviceApplications(skStatus: SkStatus): Observable<RegistrationOfEnergyProducerApplication[]> {
    return this.communicationService.getData(`${UrlSegments.DrrCcdDeviceApplication}/filter?sk_status=${skStatus}`);
  }

  getConsumptionDeviceApplication(applicationId: string): Observable<DrrCcdDeviceApplicationDto> {
    return this.communicationService.getData(`${UrlSegments.DrrCcdDeviceApplication}?application_id=${applicationId}`).pipe(
      map((r: DrrCcdDeviceApplicationDto) => {
        r.is_registration_device = parseBoolelan(r.is_registration_device);
        r.is_commissioning_done = parseBoolelan(r.is_commissioning_done);
        r.is_commissioning_confirmed = parseBoolelan(r.is_commissioning_confirmed);
        r.is_consumption_device = parseBoolelan(r.is_consumption_device);
        return r;
      })
    );
  }

  getRegepApplication(id: string): Observable<RegistrationOfEnergyProducerApplication> {
    return this.communicationService.getData(`${UrlSegments.RegistrationOfEnergyProducerApplication}/${id}`);
  }

  createInstallationApplication(selectedFlowType: string, data: any): Observable<CreateInstallationApplicationResponse> {
    const requestData: any = this.prepareInstallationRequest(selectedFlowType, data);

    return this.communicationService.postData(UrlSegments.CreateInstallationApplication, requestData);
  }

  updateInstallationApplication(selectedFlowType: string, data: any): Observable<CreateInstallationApplicationResponse> {
    const requestData: any = this.prepareInstallationRequest(selectedFlowType, data);

    return this.communicationService.updateData(`${UrlSegments.UpdateInstallationApplication}${data.applicationId}`, requestData);
  }

  createGridCompFeedInApplication(selectedFlowType: string): Observable<CreateGridCompFeedInApplicationResponse> {
    // TODO: This function may be obsolete. Consider redirecting existing function
    // calls to createGridCompApplication(requestData: any)
    const requestData = {
      sk_type: CommonService.getGridCompFeedInApplicationType(selectedFlowType),
    };
    return this.communicationService.postData(UrlSegments.CreateGridCompFeedInApplication, requestData);
  }

  createGridCompApplication(requestData: any): Observable<CreateGridCompFeedInApplicationResponse> {
    requestData = {
      ...requestData,
      sk_type: GridConnectionApplicationType.GridCompatibility,
    };
    return this.communicationService.postData(UrlSegments.CreateGridCompFeedInApplication, requestData);
  }

  updateGridCompApplication(requestData: any): Observable<CreateGridCompFeedInApplicationResponse> {
    const applicationId = requestData.application_id;
    if (!applicationId) throw new Error('applicationId is required!');

    requestData.application_id = 0; //TODO.dsafasdkfsapfdkdsaf: because of some stupidity, the application_id is forbiden in the request body. Must be provded as query parameter instead! wtf???
    requestData = {
      ...requestData,
      sk_type: GridConnectionApplicationType.GridCompatibility,
    };
    return this.communicationService.updateData(`${UrlSegments.UpdateGridCompFeedInApplication}${applicationId}`, requestData);
  }

  createRegEPApplication(requestData?: any): Observable<any> {
    return this.communicationService.postData(UrlSegments.RegistrationOfEnergyProducerApplication, requestData);
  }

  updateRegEPApplication(data: any, applicationId: string | number): Observable<any> {
    data.application_id = 0;
    return this.communicationService.updateData(`${UrlSegments.RegistrationOfEnergyProducerApplication}/${applicationId}`, data);
  }

  getInstallationApplication(applicationId: string): Observable<GetInstallationApplicationObject> {
    return this.communicationService.getData(`${UrlSegments.InstallationApplication}${applicationId}`);
  }

  getGridCompFeedInApplication(applicationId: string): Observable<GetGridCompFeedInApplicationObject> {
    return this.communicationService.getData(`${UrlSegments.GridCompFeedInApplication}${applicationId}`);
  }

  getExistingGridConnectionApplication(selectedFlowType: string): Observable<GetGridConnectionObject> {
    const applicationType = this.getGridConnectionApplicationType(selectedFlowType);
    return this.communicationService.getData(`${UrlSegments.ExistingGridConnectionApplication}${applicationType}`);
  }

  createGridConnectionApplication(requestData: CreateGridConnectionRequest): Observable<CreateGridConnectionResponse> {
    return this.communicationService.postData(UrlSegments.CreateGridConnectionApplication, requestData);
  }

  getGridConnectionApplication(applicationId: string): Observable<GetGridConnectionObject> {
    return this.communicationService.getData(`${UrlSegments.GridConnectionApplication}${applicationId}`);
  }

  getGridConnectionApplicationType(selectedFlowType: string): string {
    switch (selectedFlowType) {
      case InstallationFlowType.ChangeHouseConnection:
      case InstallationFlowType.ConversionToGas:
        return GridConnectionApplicationType.Change;
      case InstallationFlowType.MainsConnection:
        return GridConnectionApplicationType.New;
      case InstallationFlowType.TemporaryConnection:
        return GridConnectionApplicationType.Temporary;
    }
  }

  updateGridConnectionApplication(applicationId: string, requestData): Observable<GetGridConnectionObject> {
    return this.communicationService.updateData(`${UrlSegments.GridConnectionApplication}${applicationId}`, requestData);
  }

  deleteMediaObjectsFromApplication(urlSegment: string): Observable<any> {
    return this.communicationService.deleteData(urlSegment);
  }

  getTechnicianFlow(currentFlowType: string, changeConnectionType: string): FormTypesData[] {
    switch (currentFlowType) {
      case InstallationFlowType.TemporaryConnection:
        return [TechnicianFormType[3]];
      case InstallationFlowType.ChangeHouseConnection:
      case InstallationFlowType.ConversionToGas:
        return TechnicianFormType.filter(item => item.key === changeConnectionType);
      default:
        return [];
    }
  }

  updateApplicationLockStatus(isUnlocked: boolean, taId: string, requestData: any): Observable<UpdateLockStatusResponse> {
    const requestUrl = isUnlocked ? `technician_application/lock?ta_id=${taId}` : `technician_application/unlock?ta_id=${taId}`;

    return this.communicationService.updateData(requestUrl, requestData);
  }

  getAppHistoryLog(applicationId: string, appEventType: string): Observable<OrderHistoryResponse> {
    return this.communicationService.getData(`apphistory?application_id=${applicationId}&sk_app_event_type=${appEventType}`);
  }

  parseTextFromCamelCase(inputText: string): string {
    return inputText.replace(/[A-Z]/g, m => '-' + m.toLowerCase());
  }

  // to parse string of format 'yyyy-mm-dd' into 'dd.mm.yyyy'
  parseDateForUI(date: string): string {
    const dateValue = new Date(date);
    return !isNaN(dateValue.getDate())
      ? `${dateValue.getDate() < 10 ? '0' + dateValue.getDate() : dateValue.getDate()}.${
          dateValue.getMonth() + 1 < 10 ? '0' + (dateValue.getMonth() + 1) : dateValue.getMonth() + 1
        }.${dateValue.getFullYear()}`
      : date;
  }

  convertDateObject(str) {
    const date = new Date(str);
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    return [date.getFullYear(), month, day].join('-');
  }

  // to parse into string of format 'yyyy-mm-dd' for saving
  parseDateForSaving(date: string | Date): string {
    if (date instanceof Date) return this.datePipe.transform(date, 'yyyy-MM-dd');

    const dateParts = date.split('.').map(x => parseInt(x));
    const dateObj = new Date(dateParts[2], dateParts[1] - 1, dateParts[0]);
    return this.datePipe.transform(dateObj, 'yyyy-MM-dd');
  }

  updateMediaObjectsFromApplication(urlSegment: string, requestObject: any): Observable<any> {
    return this.communicationService.updateData(urlSegment, requestObject);
  }

  getAppLocationByApplicationId(applicationId: string): Observable<AppLocation> {
    return this.communicationService.getData(UrlSegments.GetAppLocationByApplicationId.replace(':applicationId', applicationId));
  }

  createAppLocation(appLocation: AppLocation): Observable<any> {
    return this.communicationService.postData(`${UrlSegments.AppLocation}`, appLocation);
  }

  updateAppLocation(appLocation: AppLocation): Observable<any> {
    return this.communicationService.updateData(`${UrlSegments.AppLocation}?id=${appLocation.id}`, appLocation);
  }

  parseLocaleFractionsToStandard(value: string): string {
    if (value.indexOf(',') > -1) {
      value = value.replace(',', '.');
    }
    return value;
  }

  getPermittedTradesData(technicianData: TechnicianData) {
    let mediumVisibility;
    const installationFlow = JSON.parse(localStorage.getItem('installation_flow'));
    if (technicianData) {
      if (technicianData.role === 'customer') {
        mediumVisibility = {
          gas: !(!this.environmentService.showGasMedia && installationFlow === InstallationFlowType.MainsConnection),
          heating: this.environmentService.showDistrictHeatMedia && installationFlow === InstallationFlowType.MainsConnection,
          power:
            this.environmentService.showPowerMediaForCustomer ||
            (!this.environmentService.showPowerMediaForCustomer && installationFlow !== InstallationFlowType.TemporaryConnection),
          water: true,
        };
      } else {
        const isInstallationFlow =
          (installationFlow === InstallationFlowType.MainsConnection || installationFlow === InstallationFlowType.ChangeHouseConnection) &&
          this.environmentService.customer === 'NSK';
        mediumVisibility = {
          ...this.environmentService.mediaToShow,
          ...(isInstallationFlow && { heating: false }),
        };
        FacilityFormType.forEach(item => {
          const medium = Object.keys(technicianData).filter(key => key.indexOf(item.key) > -1);
          if (mediumVisibility[item.key]) {
            mediumVisibility[item.key] = technicianData[medium[0]] === '1';
          }
        });
      }
    }
    this.localStorageService.setItem('mediumVisibility', mediumVisibility);
    return mediumVisibility;
  }

  initializeCustomFilter(callback: (value, filter) => boolean): void {
    /* tslint:disable:no-string-literal */
    FilterUtils['custom'] = (value, filter): boolean => {
      if (filter === undefined || filter === null || !filter || (filter?.hasOwnProperty('length') && !filter.length)) {
        return true;
      }

      if (value === undefined || value === null || (value?.hasOwnProperty('length') && !value.length)) {
        return false;
      }
      return callback(value, filter);
    };

    FilterUtils['custom_equal_not_combined'] = (value, filter: any[]): boolean => {
      if (filter === undefined || filter === null || !filter || (filter?.hasOwnProperty('length') && !filter.length)) {
        return true;
      }

      if (value === undefined || value === null || (value?.hasOwnProperty('length') && !value.length)) {
        return false;
      }

      return filter.some(f => f === value);
    };
  }

  manageStickyHeader(headerId: string): void {
    const header = document.getElementById(headerId);
    if (header) {
      const sticky = header.offsetTop;
      if (window.pageYOffset > sticky) {
        header.classList.add('sticky');
      } else {
        header.classList.remove('sticky');
      }
    }
  }

  private static getGridCompFeedInApplicationType(selectedFlowType: string): string {
    switch (selectedFlowType) {
      case InstallationFlowType.GridCompatibility:
        return GridConnectionApplicationType.GridCompatibility;
      case InstallationFlowType.RegEP:
        return GridConnectionApplicationType.RegEP;
    }
  }

  private prepareInstallationRequest(selectedFlowType: string, data: any) {
    const requestData: any = {
      sk_installation_type: '',
    };
    switch (selectedFlowType) {
      case InstallationFlowType.UpdateInstallation:
        requestData.sk_installation_type = GridConnectionApplicationType.Change;
        break;
      case InstallationFlowType.FeedInSystem:
        requestData.sk_installation_type = GridConnectionApplicationType.FeedInSystem;
        break;
      case InstallationFlowType.SiteElectricityMeterdata:
        requestData.sk_installation_type = GridConnectionApplicationType.SiteElectricityMeterdata;
        break;
      default:
        requestData.sk_installation_type = GridConnectionApplicationType.New;
        break;
    }

    if (data) {
      requestData.connectionobject = {
        area_locations: [],
      };

      if (data.area && data.field && data.field_sector)
        requestData.connectionobject.area_locations.push({
          area: data.area,
          field: data.field,
          field_sector: data.field_sector,
          postcode: data.postcode,
          other_area: data.other_area,
        });
    }
    return requestData;
  }

  createConsumptionDeviceApplication(requestData?: any): Observable<any> {
    return this.communicationService.postData(UrlSegments.DrrCcdDeviceApplication, requestData);
  }
}
